import { Skeleton, SkeletonItem } from '@fluentui/react-components';
import { Delete20Regular, PeopleTeam20Regular } from '@fluentui/react-icons';
import { AzureGroup } from '@mnspro-cloud/services';
import { GroupRole } from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { first } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LazyAvatarGroup } from '../Avatar';
import { Button } from '../Button';
import { Flex } from '../Flex';
import { GroupPicker } from '../GroupPicker/GroupPicker';
import { List } from '../List';
import { Text } from '../Text';
import { buildAzureGroupListItem } from './buildAzureGroupListItem';

import styles from './AzureGroupSelector.module.css';

export interface AzureGroupSelectorProps {
  azureGroup?: AzureGroup;
  groups: AzureGroup[];
  isLoading?: boolean;
  isLoadingGroups?: boolean;
  className?: string;
  /**
   * Role(s) this selector is being used for. When set, groups whose role
   * is not in the list are shown disabled so the user can see why they
   * cannot be picked. Accepts a single GroupRole or an array.
   */
  expectedRole?: GroupRole | GroupRole[];
  onSelectGroupClicked?(): void;
  onUpdate(azureGroup?: AzureGroup): void;
}

export const AzureGroupSelector: React.FC<AzureGroupSelectorProps> = ({
  azureGroup,
  groups,
  isLoading,
  isLoadingGroups,
  className,
  expectedRole,
  onSelectGroupClicked,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [showGroupPicker, setShowGroupPicker] = useState(false);

  return (
    <>
      <GroupPicker
        open={showGroupPicker}
        canceled={() => setShowGroupPicker(false)}
        groupsSelected={(selectedGroups) => {
          const selected = first(selectedGroups);

          if (selected === undefined) {
            return;
          }

          const original = groups.find((g) => g.id === selected.id);

          onUpdate({
            id: selected.id,
            name: original?.name ?? selected.name,
            mailAddress: selected.mailAddress,
            role: original?.role,
            timePeriod: original?.timePeriod,
          });
          setShowGroupPicker(false);
        }}
        groups={groups.map((g) => buildAzureGroupListItem(g, expectedRole, t))}
        isLoading={isLoadingGroups ?? false}
      />
      <Flex direction="column" className={className}>
        {isLoading && (
          <Skeleton data-testid="titleSkeleton">
            <SkeletonItem style={{ width: '100px', height: '20px' }} className={styles.title} />
          </Skeleton>
        )}
        {!isLoading && (
          <Text data-testid="title" weight="bold" style={{ marginBottom: '0.625rem' }}>
            {t('settings:classbook:course:linked_azure_group', {
              defaultValue: 'Verknüpfte Gruppe',
            })}
          </Text>
        )}
        {isLoading && (
          <Skeleton data-testid="groupSkeleton" className={styles.skeletonGroup}>
            <SkeletonItem style={{ width: '100%', height: '44px' }} />
          </Skeleton>
        )}
        {!isLoading &&
          (!azureGroup ? (
            <Button
              data-testid="select-group-button"
              className={styles.selectButton}
              icon={<PeopleTeam20Regular />}
              appearance="primary"
              onClick={() => {
                onSelectGroupClicked?.();
                setShowGroupPicker(true);
              }}
            >
              {t('settings:classbook:course:select_azure_group', {
                defaultValue: 'Gruppe auswählen...',
              })}
            </Button>
          ) : (
            <List
              className={styles.list}
              entries={[
                {
                  label: azureGroup.name,
                  content: azureGroup.mailAddress,
                  avatar: (
                    <LazyAvatarGroup
                      shape="square"
                      name={azureGroup?.name}
                      groupId={azureGroup.id}
                    />
                  ),
                  actions: [
                    {
                      icon: <Delete20Regular />,
                      onClick: () => onUpdate(),
                    },
                  ],
                },
              ]}
            />
          ))}
      </Flex>
    </>
  );
};
